import React from 'react';
import { Badge, Button } from '@odin-labs/components';
import {
  AccessGroupSummaryFragment,
  AccessGroupSyncState,
  ConnectionOrigin,
} from 'apollo/generated/client-operations';
import { formatAccessDate, zoneStateLabels } from './presentation';
import type { useAccessGroupAction } from './useAccessGroupAction';

type Group = AccessGroupSummaryFragment;

export function AccessGroupStatus({
  sync,
  timeZone,
}: {
  timeZone: string;
  sync: Group['avigilon'];
}): React.ReactElement {
  switch (sync.state) {
    case AccessGroupSyncState.Off:
      return <span>Off · not applied to hardware</span>;
    case AccessGroupSyncState.Pending:
      return <span>Sync pending</span>;
    case AccessGroupSyncState.Failed:
      return (
        <span className="odin-text-red-700">
          Sync failed: {sync.error || 'Provider update failed'}
        </span>
      );
    case AccessGroupSyncState.Synced:
      return <span>Synced {formatAccessDate(sync.syncedAt, timeZone)}</span>;
    default: {
      const exhaustive: never = sync.state;
      return exhaustive;
    }
  }
}

export function ProviderDetails({
  group,
  timeZone,
}: {
  timeZone: string;
  group: Group;
}): React.ReactElement {
  return (
    <div className="odin-rounded-lg odin-bg-gray-50 odin-p-4 odin-space-y-2 odin-text-sm">
      <h3 className="odin-font-semibold">Avigilon Alta</h3>
      <p>
        <AccessGroupStatus sync={group.avigilon} timeZone={timeZone} />
      </p>
      <p>Provider name: {group.avigilon.name || 'Not connected yet'}</p>
      {group.avigilon.origin && (
        <p>
          {group.avigilon.origin === ConnectionOrigin.Adopted
            ? 'Adopted group · its Avigilon name is preserved.'
            : 'Created by Odin'}
        </p>
      )}
      <p className="odin-text-gray-600">
        Group sync reports configuration. It does not confirm a worker’s access
        at a reader.
      </p>
    </div>
  );
}

export function GroupZones({
  zones,
}: {
  zones: Group['zones'];
}): React.ReactElement {
  if (!zones.length)
    return <span className="odin-text-gray-500">No zones granted</span>;
  return (
    <div className="odin-flex odin-flex-wrap odin-gap-2">
      {zones.map((grant) => (
        <Badge
          key={grant.id}
          text={`${grant.name} · ${zoneStateLabels[grant.state]}`}
          color="gray"
        />
      ))}
    </div>
  );
}

export function ActionFeedback({
  error,
  message,
}: {
  error?: string | null;
  message?: string | null;
}): React.ReactElement {
  return (
    <>
      {error && (
        <p
          role="alert"
          className="odin-rounded odin-bg-red-50 odin-p-3 odin-text-sm odin-text-red-700"
        >
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="odin-text-sm odin-text-green-700">
          {message}
        </p>
      )}
    </>
  );
}

export function AccessGroupActionFeedback({
  action,
}: {
  action: ReturnType<typeof useAccessGroupAction>;
}): React.ReactElement {
  return (
    <>
      <ActionFeedback error={action.error} message={action.message} />
      {action.needsRefresh && (
        <Button
          text="Reload data"
          theme="secondary"
          disabled={action.busy}
          withSpinner={action.busy}
          onClick={() => {
            void action.retryRefresh();
          }}
        />
      )}
    </>
  );
}

export function AccessGroupsError({
  retry,
}: {
  retry: () => void;
}): React.ReactElement {
  return (
    <div className="odin-space-y-3 odin-p-6">
      <p role="alert">
        Access groups could not be loaded. Check your site permissions or try
        again.
      </p>
      <Button text="Try again" theme="secondary" onClick={retry} />
    </div>
  );
}
